import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import {
  TrendingUp, Search, Globe, Star, ArrowRight, CheckCircle2, XCircle,
  Briefcase, RefreshCw, Sparkles, Calendar, Eye, Upload, DollarSign,
  Clock, ChevronRight, BarChart3, GitCompare, ArrowUpDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

function ScoreRing({ score, size = 80 }) {
  const radius = (size - 8) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;
  const color = score >= 75 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444';
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#E5E7EB" strokeWidth="4" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset} className="transition-all duration-1000" />
      </svg>
      <span className="absolute text-lg font-bold" style={{ color }}>{score}</span>
    </div>
  );
}

export default function GrowthDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('Growth Hub');
  const [view, setView] = useState('hub');  // hub | audit | websites | referrals | trends | compare | payouts | health
  const [recs, setRecs] = useState(null);
  const [audits, setAudits] = useState([]);
  const [partners, setPartners] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [trends, setTrends] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [hostingPlans, setHostingPlans] = useState([]);
  const [myReferrals, setMyReferrals] = useState([]);
  const [payouts, setPayouts] = useState(null);
  const [loading, setLoading] = useState(true);

  // Compare state
  const [compareSelected, setCompareSelected] = useState([]);
  const [compareResult, setCompareResult] = useState(null);
  const [comparing, setComparing] = useState(false);

  // Forms
  const [auditForm, setAuditForm] = useState({ business_name: '', industry: 'General', website: '', location: '', state: '', google_profile_url: '', license_number: '', ein: '', years_in_business: '', notes: '' });
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [gbpChecklist, setGbpChecklist] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  const [gbpCheckState, setGbpCheckState] = useState({});
  const [webForm, setWebForm] = useState({ business_name: '', industry: '', description: '' });
  const [generating, setGenerating] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [refForm, setRefForm] = useState({ service_needed: '', description: '', budget_range: '' });
  const [scheduleForm, setScheduleForm] = useState({ frequency: 'monthly' });

  useEffect(() => {
    (async () => {
      try {
        const results = await Promise.allSettled([
          api.get('/api/growth/recommendations'),
          api.get('/api/growth/audits'),
          api.get('/api/growth/schedules'),
          api.get('/api/growth/trends'),
          api.get('/api/referrals/partners'),
          api.get('/api/websites/templates'),
          api.get('/api/websites/industries'),
          api.get('/api/websites/hosting-plans'),
          api.get('/api/referrals/my-referrals'),
          user?.role === 'platform_admin' ? api.get('/api/referrals/payouts') : Promise.resolve({ data: null }),
        ]);
        const d = (i) => results[i].status === 'fulfilled' ? results[i].value.data : null;
        setRecs(d(0) || { has_audit: false });
        setAudits(d(1)?.audits || []);
        setSchedules(d(2)?.schedules || []);
        setTrends(d(3));
        setPartners(d(4)?.partners || []);
        setTemplates(d(5)?.templates || []);
        setIndustries(d(6)?.industries || []);
        setHostingPlans(d(7)?.plans || []);
        setMyReferrals(d(8)?.referrals || []);
        if (d(9)) setPayouts(d(9));
      } catch (e) { /* handled */ } finally { setLoading(false); }
    })();
  }, [user?.role]);

  useEffect(() => {
    if (searchParams.get('hosting') === 'success') toast.success('Hosting plan purchased!');
    if (searchParams.get('payout') === 'success') toast.success('Payout processed!');
  }, [searchParams]);

  const handleRunAudit = async () => {
    if (!auditForm.business_name) { toast.error('Business name required'); return; }
    setAuditing(true);
    try {
      const { data } = await api.post('/api/growth/audit', auditForm);
      setAuditResult(data);
      setAudits(prev => [data, ...prev]);
      const tRes = await api.get('/api/growth/trends');
      setTrends(tRes.data);
      toast.success('Business audit complete!');
    } catch { toast.error('Audit failed'); }
    finally { setAuditing(false); }
  };

  const handleCreateSchedule = async () => {
    if (!auditForm.business_name) { toast.error('Run an audit first'); return; }
    try {
      const { data } = await api.post('/api/growth/schedules', { ...auditForm, ...scheduleForm });
      setSchedules(prev => [data, ...prev]);
      toast.success(`${scheduleForm.frequency} audit scheduled!`);
    } catch { toast.error('Schedule creation failed'); }
  };

  const handleToggleSchedule = async (id, active) => {
    try {
      await api.put(`/api/growth/schedules/${id}`, { active: !active });
      setSchedules(prev => prev.map(s => s.id === id ? { ...s, active: !active } : s));
      toast.success(active ? 'Schedule paused' : 'Schedule activated');
    } catch { toast.error('Update failed'); }
  };

  const handleCompare = async () => {
    if (compareSelected.length < 2) { toast.error('Select at least 2 audits to compare'); return; }
    setComparing(true);
    try {
      const { data } = await api.post('/api/growth/compare', { audit_ids: compareSelected });
      setCompareResult(data);
    } catch { toast.error('Comparison failed'); }
    finally { setComparing(false); }
  };

  const toggleCompareAudit = (id) => {
    setCompareSelected(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : prev.length < 4 ? [...prev, id] : prev
    );
    setCompareResult(null);
  };

  const handleGenWebsite = async () => {
    if (!webForm.business_name) { toast.error('Business name required'); return; }
    setGenerating(true);
    try {
      const { data } = await api.post('/api/websites/generate', webForm);
      setTemplates(prev => [data, ...prev]);
      toast.success('Website template generated!');
    } catch { toast.error('Generation failed'); }
    finally { setGenerating(false); }
  };

  const handlePublish = async (templateId) => {
    try {
      await api.post(`/api/websites/templates/${templateId}/publish`);
      setTemplates(prev => prev.map(t => t.id === templateId ? { ...t, status: 'published' } : t));
      toast.success('Website published!');
    } catch { toast.error('Publish failed'); }
  };

  const handleHostingCheckout = async (planId, templateId) => {
    try {
      const { data } = await api.post('/api/websites/hosting/checkout', {
        plan_id: planId, template_id: templateId, origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
    } catch { toast.error('Checkout failed'); }
  };

  const handleSubmitReferral = async () => {
    if (!selectedPartner) return;
    try {
      const { data } = await api.post('/api/referrals/submit', {
        partner_id: selectedPartner.id, partner_name: selectedPartner.name, ...refForm,
      });
      setMyReferrals(prev => [data, ...prev]);
      toast.success('Referral submitted!');
      setSelectedPartner(null);
      setView('hub');
    } catch { toast.error('Submission failed'); }
  };

  const latestAudit = auditResult || (audits.length > 0 ? audits[0] : null);
  const analysis = latestAudit?.analysis;
  const score = analysis?.overall_score || recs?.overall_score || 0;

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="growth-dashboard">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          {/* Header + Sub-nav */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">Growth Hub</h1>
              <p className="text-sm text-gray-500 mt-1">Audit, improve, and grow your digital presence</p>
            </div>
          </div>
          <div className="flex gap-1 overflow-x-auto border-b border-gray-200 pb-0">
            {['hub', 'audit', 'health', 'trends', 'compare', 'websites', 'referrals', ...(user?.role === 'platform_admin' ? ['payouts'] : [])].map(tab => (
              <button key={tab} onClick={() => setView(tab)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors capitalize whitespace-nowrap ${view === tab ? 'border-[#D4A843] text-[#D4A843]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                data-testid={`growth-tab-${tab}`}
              >{tab === 'hub' ? 'Overview' : tab}</button>
            ))}
          </div>

          {/* ── HUB VIEW ─────────────────────────────────────── */}
          {view === 'hub' && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col items-center" data-testid="score-card">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Digital Presence Score</p>
                  <ScoreRing score={score} size={100} />
                  <p className="text-sm font-semibold text-gray-900 mt-3">{recs?.business_name || 'Run an audit to see your score'}</p>
                  {trends?.deltas?.overall !== undefined && (
                    <span className={`text-xs font-bold mt-1 ${trends.deltas.overall >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {trends.deltas.overall >= 0 ? '+' : ''}{trends.deltas.overall} pts since first audit
                    </span>
                  )}
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="quick-wins">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Quick Wins</h3>
                  {(recs?.quick_wins || []).length > 0 ? (
                    <ul className="space-y-2">{recs.quick_wins.map((w, i) => (
                      <li key={`item-${i}`} className="flex items-start gap-2 text-sm text-gray-600"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" strokeWidth={2} />{w}</li>
                    ))}</ul>
                  ) : <p className="text-sm text-gray-400">Complete an audit to see quick wins</p>}
                </div>
                <div className="space-y-3">
                  {[
                    { v: 'audit', icon: Search, bg: 'bg-[#D4A843]/10', ic: 'text-[#D4A843]', t: 'Profile Audit', d: 'Analyze digital presence' },
                    { v: 'websites', icon: Globe, bg: 'bg-emerald-100', ic: 'text-emerald-600', t: 'Website Starter', d: 'Generate & publish site' },
                    { v: 'referrals', icon: Briefcase, bg: 'bg-amber-100', ic: 'text-amber-600', t: 'Find a Partner', d: 'Trusted service providers' },
                    { v: 'trends', icon: TrendingUp, bg: 'bg-violet-100', ic: 'text-violet-600', t: 'Score Trends', d: 'Track improvements' },
                  ].map(a => (
                    <button key={a.v} onClick={() => setView(a.v)} className="w-full bg-white border border-gray-200 rounded-lg p-4 text-left hover:border-[#D4A843] transition-colors group" data-testid={`action-${a.v}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${a.bg} flex items-center justify-center`}><a.icon className={`w-5 h-5 ${a.ic}`} /></div>
                        <div><p className="text-sm font-semibold text-gray-900">{a.t}</p><p className="text-xs text-gray-500">{a.d}</p></div>
                        <ArrowRight className="w-4 h-4 text-gray-400 ml-auto group-hover:text-[#D4A843]" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Schedules */}
              {schedules.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="schedules-list">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-[#D4A843]" /> Active Schedules</h3>
                  <div className="space-y-2">{schedules.map(s => (
                    <div key={s.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{s.business_info?.name}</p>
                        <p className="text-xs text-gray-400">{s.frequency} | Next: {s.next_run_at ? new Date(s.next_run_at).toLocaleDateString() : 'N/A'} | Runs: {s.run_count}</p>
                      </div>
                      <button onClick={() => handleToggleSchedule(s.id, s.active)}
                        className={`w-11 h-6 rounded-full transition-colors ${s.active ? 'bg-[#D4A843]' : 'bg-gray-300'}`}
                        data-testid={`schedule-toggle-${s.id}`}
                      ><div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${s.active ? 'translate-x-5' : 'translate-x-0.5'}`} /></button>
                    </div>
                  ))}</div>
                </div>
              )}
            </>
          )}

          {/* ── AUDIT VIEW ───────────────────────────────────── */}
          {view === 'audit' && (
            <>
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="audit-form">
                <h3 className="font-heading text-lg font-bold text-gray-900 mb-4">Business Profile Audit</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div><Label className="text-xs">Business Name *</Label><Input value={auditForm.business_name} onChange={e => setAuditForm({...auditForm, business_name: e.target.value})} className="mt-1 rounded-lg" data-testid="audit-name" /></div>
                  <div><Label className="text-xs">Industry</Label><Input value={auditForm.industry} onChange={e => setAuditForm({...auditForm, industry: e.target.value})} className="mt-1 rounded-lg" data-testid="audit-industry" /></div>
                  <div><Label className="text-xs">Website URL</Label><Input value={auditForm.website} onChange={e => setAuditForm({...auditForm, website: e.target.value})} className="mt-1 rounded-lg" placeholder="https://..." data-testid="audit-website" /></div>
                  <div><Label className="text-xs">Location</Label><Input value={auditForm.location} onChange={e => setAuditForm({...auditForm, location: e.target.value})} className="mt-1 rounded-lg" data-testid="audit-location" /></div>
                  <div><Label className="text-xs">State</Label><Input value={auditForm.state} onChange={e => setAuditForm({...auditForm, state: e.target.value})} className="mt-1 rounded-lg" placeholder="e.g., Colorado" data-testid="audit-state" /></div>
                  <div className="md:col-span-2"><Label className="text-xs">Google Business Profile URL</Label><Input value={auditForm.google_profile_url} onChange={e => setAuditForm({...auditForm, google_profile_url: e.target.value})} className="mt-1 rounded-lg" data-testid="audit-gbp" /></div>
                  <div><Label className="text-xs">Business License #</Label><Input value={auditForm.license_number} onChange={e => setAuditForm({...auditForm, license_number: e.target.value})} className="mt-1 rounded-lg font-mono" data-testid="audit-license" /></div>
                  <div><Label className="text-xs">EIN / Tax ID</Label><Input value={auditForm.ein} onChange={e => setAuditForm({...auditForm, ein: e.target.value})} className="mt-1 rounded-lg font-mono" data-testid="audit-ein" /></div>
                  <div><Label className="text-xs">Years in Business</Label><Input type="number" value={auditForm.years_in_business} onChange={e => setAuditForm({...auditForm, years_in_business: e.target.value})} className="mt-1 rounded-lg" data-testid="audit-years" /></div>
                </div>
                <div className="flex gap-2 items-end">
                  <Button onClick={handleRunAudit} disabled={auditing} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="run-audit-btn">
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />{auditing ? 'Analyzing...' : 'Run Audit'}
                  </Button>
                  <div className="flex items-center gap-2 ml-4">
                    <Select value={scheduleForm.frequency} onValueChange={v => setScheduleForm({ frequency: v })}>
                      <SelectTrigger className="w-32 rounded-lg h-9 text-xs" data-testid="schedule-freq"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={handleCreateSchedule} className="rounded-lg text-xs" data-testid="create-schedule-btn">
                      <Calendar className="w-3 h-3 mr-1" /> Schedule
                    </Button>
                  </div>
                </div>
              </div>

              {analysis && !analysis.error && (
                <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="audit-results">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg font-bold text-gray-900">Audit: {latestAudit?.business_info?.name}</h3>
                    <span className="text-xs text-gray-400 font-mono">GPT-5.2 | {new Date(analysis.analyzed_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">{analysis.summary}</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                    {Object.entries(analysis.categories || {}).map(([key, cat]) => (
                      <div key={key} className="text-center p-3 rounded-lg bg-gray-50">
                        <ScoreRing score={typeof cat === 'object' ? cat.score : 0} size={56} />
                        <p className="text-[10px] text-gray-500 mt-1 capitalize">{key.replace(/_/g, ' ')}</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-xs font-bold text-red-600 uppercase tracking-wider mb-2">Missing Items</h4>
                      <ul className="space-y-1">{(analysis.missing_items || []).map((m, i) => (
                        <li key={`item-${i}`} className="flex items-start gap-2 text-xs text-gray-600"><XCircle className="w-3 h-3 text-red-400 shrink-0 mt-0.5" />{m}</li>
                      ))}</ul>
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-[#D4A843] uppercase tracking-wider mb-2">Top Recommendations</h4>
                      <ul className="space-y-1">{(analysis.top_recommendations || []).map((r, i) => (
                        <li key={`item-${i}`} className="flex items-start gap-2 text-xs text-gray-600"><CheckCircle2 className="w-3 h-3 text-[#D4A843] shrink-0 mt-0.5" />{r}</li>
                      ))}</ul>
                    </div>
                  </div>
                </div>
              )}

              {audits.length > 1 && (
                <div>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Audit History</h3>
                  <div className="space-y-2">{audits.slice(1).map(a => (
                    <div key={a.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-testid={`audit-${a.id}`}>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{a.business_info?.name}</p>
                        <p className="text-xs text-gray-400">{new Date(a.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <ScoreRing score={a.analysis?.overall_score || 0} size={40} />
                        {a.schedule_id && <span className="text-[10px] bg-[#D4A843]/10 text-[#D4A843] px-1.5 py-0.5 rounded font-medium">Scheduled</span>}
                      </div>
                    </div>
                  ))}</div>
                </div>
              )}
            </>
          )}

          {/* ── TRENDS VIEW ──────────────────────────────────── */}
          {view === 'trends' && (
            <div className="space-y-6" data-testid="trends-view">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-heading text-lg font-bold text-gray-900 mb-4">Score Trend Over Time</h3>
                {(trends?.trends || []).length >= 2 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={(trends.trends || []).map(t => ({
                        ...t,
                        date: new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                        <Line type="monotone" dataKey="overall_score" stroke="#D4A843" strokeWidth={2.5} dot={{ r: 4, fill: '#D4A843' }} name="Overall Score" />
                        <Line type="monotone" dataKey="online_presence" stroke="#10B981" strokeWidth={1.5} dot={false} name="Online Presence" />
                        <Line type="monotone" dataKey="seo_readiness" stroke="#F59E0B" strokeWidth={1.5} dot={false} name="SEO Readiness" />
                        <Line type="monotone" dataKey="content_quality" stroke="#8B5CF6" strokeWidth={1.5} dot={false} name="Content Quality" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BarChart3 className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-400">Run at least 2 audits to see trends</p>
                    <Button onClick={() => setView('audit')} variant="outline" className="mt-3 text-xs rounded-lg">Run an Audit</Button>
                  </div>
                )}
              </div>

              {trends?.deltas && Object.keys(trends.deltas).length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(trends.deltas).map(([key, val]) => (
                    <div key={key} className="bg-white border border-gray-200 rounded-lg p-4 text-center">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className={`text-xl font-bold mt-1 ${val >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {val >= 0 ? '+' : ''}{val}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── WEBSITES VIEW ────────────────────────────────── */}
          {view === 'websites' && (
            <div className="space-y-6" data-testid="websites-view">
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="website-form">
                <h3 className="font-heading text-lg font-bold text-gray-900 mb-4">Website Starter Generator</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div><Label className="text-xs">Business Name *</Label><Input value={webForm.business_name} onChange={e => setWebForm({...webForm, business_name: e.target.value})} className="mt-1 rounded-lg" data-testid="web-name" /></div>
                  <div>
                    <Label className="text-xs">Industry</Label>
                    <Select value={webForm.industry} onValueChange={v => setWebForm({...webForm, industry: v})}>
                      <SelectTrigger className="mt-1 rounded-lg" data-testid="web-industry"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{industries.map(i => <SelectItem key={i.id} value={i.name}>{i.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2"><Label className="text-xs">Description</Label><Textarea value={webForm.description} onChange={e => setWebForm({...webForm, description: e.target.value})} className="mt-1 rounded-lg" rows={3} data-testid="web-desc" /></div>
                </div>
                <Button onClick={handleGenWebsite} disabled={generating} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid="gen-website-btn">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />{generating ? 'Generating...' : 'Generate Template'}
                </Button>
              </div>

              {templates.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Your Templates</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {templates.map(t => (
                      <div key={t.id} className="bg-white border border-gray-200 rounded-lg p-5 card-hover" data-testid={`template-${t.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-emerald-500" /><span className="text-xs text-gray-400">{t.industry}</span></div>
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${t.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{t.status}</span>
                        </div>
                        <h4 className="text-sm font-bold text-gray-900">{t.business_name}</h4>
                        {t.content?.hero_headline && <p className="text-xs text-[#D4A843] mt-1 font-medium truncate">"{t.content.hero_headline}"</p>}
                        <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                          <Button size="sm" variant="outline" className="text-xs rounded" data-testid={`preview-${t.id}`} onClick={async () => {
                            try {
                              const { data } = await api.get('/api/auth/ws-ticket');
                              const tok = data?.token || '';
                              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/websites/templates/${t.id}/preview?token=${tok}`, '_blank', 'noopener,noreferrer');
                            } catch (err) {
                              console.warn('[preview] ticket fetch failed', err);
                            }
                          }}><Eye className="w-3 h-3 mr-1" />Preview</Button>
                          {t.status !== 'published' && (
                            <Button size="sm" onClick={() => handlePublish(t.id)} className="text-xs rounded bg-emerald-600 text-white" data-testid={`publish-${t.id}`}><Upload className="w-3 h-3 mr-1" />Publish</Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hosting Upsell */}
              {templates.length > 0 && (
                <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-xl p-8 text-white" data-testid="hosting-upsell">
                  <h3 className="font-heading text-xl font-bold mb-2">Need professional hosting?</h3>
                  <p className="text-sm text-gray-300 mb-6">Get your generated site live with a custom domain, SSL, and managed hosting.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {hostingPlans.map(plan => (
                      <div key={plan.id} className="bg-white/10 backdrop-blur rounded-lg p-5 border border-white/20" data-testid={`hosting-${plan.id}`}>
                        <h4 className="text-sm font-bold">{plan.name}</h4>
                        <p className="text-2xl font-bold mt-1">${plan.price}<span className="text-xs font-normal text-gray-300">{plan.id === 'managed' ? '' : '/mo'}</span></p>
                        <ul className="mt-3 space-y-1">{plan.features.map((f, i) => (
                          <li key={`item-${i}`} className="flex items-start gap-1.5 text-xs text-gray-300"><CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0 mt-0.5" />{f}</li>
                        ))}</ul>
                        <Button size="sm" className="w-full mt-4 bg-white text-[#0F172A] hover:bg-gray-100 rounded-lg text-xs font-semibold"
                          onClick={() => handleHostingCheckout(plan.id, templates[0]?.id)}
                          data-testid={`checkout-${plan.id}`}
                        >Get Started</Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── REFERRALS VIEW ───────────────────────────────── */}
          {view === 'referrals' && (
            <div className="space-y-6" data-testid="referrals-view">
              {!selectedPartner ? (
                <div>
                  <h3 className="font-heading text-lg font-bold text-gray-900 mb-4">Trusted Partners</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {partners.map(p => (
                      <button key={p.id} onClick={() => setSelectedPartner(p)} className="text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-[#D4A843] transition-colors" data-testid={`partner-${p.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-bold text-gray-900">{p.name}</h4>
                          <div className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-400" fill="currentColor" strokeWidth={0} /><span className="text-xs font-bold">{p.rating}</span></div>
                        </div>
                        <p className="text-xs text-gray-500 mb-2 line-clamp-2">{p.description}</p>
                        <div className="flex flex-wrap gap-1">{(p.services || []).slice(0, 3).map((s, i) => (
                          <span key={`item-${i}`} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{s}</span>
                        ))}</div>
                        <p className="text-[10px] text-emerald-600 font-semibold mt-2">{p.commission_pct}% referral commission</p>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="referral-form">
                  <div className="bg-[#D4A843]/5 rounded-lg p-3 flex items-center justify-between mb-4">
                    <div><p className="text-sm font-bold text-[#D4A843]">{selectedPartner.name}</p><p className="text-xs text-gray-500">{selectedPartner.category} | {selectedPartner.commission_pct}% commission</p></div>
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => setSelectedPartner(null)}>Change</Button>
                  </div>
                  <div className="space-y-4">
                    <div><Label className="text-xs">Service Needed</Label><Input value={refForm.service_needed} onChange={e => setRefForm({...refForm, service_needed: e.target.value})} className="mt-1 rounded-lg" data-testid="ref-service" /></div>
                    <div><Label className="text-xs">Description</Label><Textarea value={refForm.description} onChange={e => setRefForm({...refForm, description: e.target.value})} className="mt-1 rounded-lg" rows={3} data-testid="ref-desc" /></div>
                    <div><Label className="text-xs">Budget Range</Label>
                      <Select value={refForm.budget_range} onValueChange={v => setRefForm({...refForm, budget_range: v})}>
                        <SelectTrigger className="mt-1 rounded-lg w-48" data-testid="ref-budget"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {['< $500', '$500 - $2,000', '$2,000 - $5,000', '$5,000 - $10,000', '$10,000+'].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleSubmitReferral} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="submit-referral-btn">Submit Referral</Button>
                  </div>
                </div>
              )}

              {myReferrals.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">My Referrals</h3>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm"><thead><tr className="border-b bg-gray-50">
                      <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Partner</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Service</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Budget</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Payout</th>
                    </tr></thead><tbody className="divide-y divide-gray-100">
                      {myReferrals.map(r => (
                        <tr key={r.id}><td className="px-4 py-2 font-medium">{r.partner_name}</td>
                        <td className="px-4 py-2 text-gray-600">{r.service_needed}</td>
                        <td className="px-4 py-2 text-gray-600">{r.budget_range}</td>
                        <td className="px-4 py-2"><span className="text-xs font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-700">{r.status}</span></td>
                        <td className="px-4 py-2 text-gray-600">{r.payout_amount ? `$${r.payout_amount}` : '—'}</td></tr>
                      ))}
                    </tbody></table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── COMPARE VIEW ─────────────────────────────────── */}
          {view === 'compare' && (
            <div className="space-y-6" data-testid="compare-view">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-heading text-lg font-bold text-gray-900 mb-1">Compare Audits</h3>
                <p className="text-sm text-gray-500 mb-4">Select 2-4 audits to compare side-by-side</p>

                {audits.length < 2 ? (
                  <div className="text-center py-12">
                    <GitCompare className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-400">You need at least 2 completed audits to compare</p>
                    <Button onClick={() => setView('audit')} variant="outline" className="mt-3 text-xs rounded-lg" data-testid="go-to-audit-btn">Run an Audit</Button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2 mb-4 max-h-[260px] overflow-y-auto">
                      {audits.map(a => (
                        <button
                          key={a.id}
                          onClick={() => toggleCompareAudit(a.id)}
                          className={`w-full text-left p-3 rounded-lg border transition-colors flex items-center justify-between ${
                            compareSelected.includes(a.id) ? 'border-[#D4A843] bg-[#D4A843]/5' : 'border-gray-100 hover:bg-gray-50'
                          }`}
                          data-testid={`compare-select-${a.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded flex items-center justify-center text-xs font-bold ${
                              compareSelected.includes(a.id) ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-gray-200 text-gray-500'
                            }`}>
                              {compareSelected.includes(a.id) ? compareSelected.indexOf(a.id) + 1 : ''}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{a.business_info?.name || 'Unnamed'}</p>
                              <p className="text-xs text-gray-400">{new Date(a.created_at).toLocaleDateString()} | Score: {a.analysis?.overall_score ?? '—'}</p>
                            </div>
                          </div>
                          <ScoreRing score={a.analysis?.overall_score || 0} size={36} />
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-3">
                      <Button onClick={handleCompare} disabled={comparing || compareSelected.length < 2} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="run-compare-btn">
                        <GitCompare className="w-3.5 h-3.5 mr-1.5" />{comparing ? 'Comparing...' : `Compare ${compareSelected.length} Audits`}
                      </Button>
                      {compareSelected.length > 0 && (
                        <Button variant="outline" size="sm" className="text-xs rounded-lg" onClick={() => { setCompareSelected([]); setCompareResult(null); }} data-testid="clear-compare-btn">Clear</Button>
                      )}
                      <span className="text-xs text-gray-400">{compareSelected.length}/4 selected</span>
                    </div>
                  </>
                )}
              </div>

              {/* Comparison Results */}
              {compareResult && (
                <div className="space-y-6 animate-fade-in" data-testid="compare-results">
                  {/* Overall Scores */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <ArrowUpDown className="w-4 h-4 text-[#D4A843]" /> Overall Scores
                    </h3>
                    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${compareResult.audits.length}, 1fr)` }}>
                      {compareResult.audits.map((a, i) => (
                        <div key={a.audit_id} className={`text-center p-4 rounded-lg border ${a.audit_id === compareResult.best_audit_id ? 'border-emerald-300 bg-emerald-50' : a.audit_id === compareResult.worst_audit_id ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`} data-testid={`compare-overall-${i}`}>
                          {a.audit_id === compareResult.best_audit_id && <span className="inline-block text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 rounded mb-2">Best</span>}
                          {a.audit_id === compareResult.worst_audit_id && compareResult.audits.length > 2 && <span className="inline-block text-[10px] font-bold uppercase tracking-wider bg-red-500 text-white px-2 py-0.5 rounded mb-2">Needs Work</span>}
                          <ScoreRing score={a.overall_score} size={72} />
                          <p className="text-sm font-semibold text-gray-900 mt-2">{a.business_name}</p>
                          <p className="text-[10px] text-gray-400">{new Date(a.date).toLocaleDateString()}</p>
                          <div className="flex justify-center gap-3 mt-2 text-[10px] text-gray-500">
                            <span>{a.missing_count} missing</span>
                            <span>{a.recommendation_count} recs</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {compareResult.overall_delta !== null && (
                      <p className={`text-center text-sm font-bold mt-4 ${compareResult.overall_delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {compareResult.overall_delta >= 0 ? '+' : ''}{compareResult.overall_delta} pts overall change (first to last)
                      </p>
                    )}
                  </div>

                  {/* Category Breakdown */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Category Breakdown</h3>
                    <div className="space-y-4">
                      {Object.entries(compareResult.category_comparison).map(([cat, data]) => (
                        <div key={cat} className="p-4 rounded-lg bg-gray-50">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-semibold text-gray-900 capitalize">{cat.replace(/_/g, ' ')}</p>
                            <div className="flex items-center gap-3 text-xs">
                              <span className="text-gray-500">Avg: <span className="font-bold text-gray-700">{data.avg}</span></span>
                              <span className={`font-bold ${data.delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {data.delta >= 0 ? '+' : ''}{data.delta}
                              </span>
                            </div>
                          </div>
                          <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${compareResult.audits.length}, 1fr)` }}>
                            {compareResult.audits.map((a, i) => {
                              const score = a.categories[cat] || 0;
                              const isMax = score === data.max;
                              const isMin = score === data.min && data.max !== data.min;
                              return (
                                <div key={a.audit_id} className="text-center" data-testid={`compare-cat-${cat}-${i}`}>
                                  <div className="h-2 rounded-full bg-gray-200 mb-1.5">
                                    <div className={`h-full rounded-full transition-all ${isMax ? 'bg-emerald-500' : isMin ? 'bg-red-400' : 'bg-[#D4A843]'}`} style={{ width: `${score}%` }} />
                                  </div>
                                  <span className={`text-xs font-bold ${isMax ? 'text-emerald-600' : isMin ? 'text-red-500' : 'text-gray-700'}`}>{score}</span>
                                  <p className="text-[10px] text-gray-400 truncate">{a.business_name}</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}


          {/* ── PAYOUTS VIEW (Admin) ─────────────────────────── */}
          {view === 'payouts' && payouts && (
            <div className="space-y-6" data-testid="payouts-view">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Total Completed</p>
                  <p className="font-heading text-2xl font-bold text-gray-900 mt-1">{payouts.total_completed}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
                  <p className="text-xs text-amber-600 font-semibold uppercase tracking-wider">Owed</p>
                  <p className="font-heading text-2xl font-bold text-amber-700 mt-1">${payouts.total_owed}</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5">
                  <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wider">Paid</p>
                  <p className="font-heading text-2xl font-bold text-emerald-700 mt-1">${payouts.total_paid}</p>
                </div>
              </div>
              {payouts.payouts?.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm"><thead><tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Partner</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Service</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Commission</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Payout</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Processed</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr></thead><tbody className="divide-y divide-gray-100">
                    {payouts.payouts.map(p => (
                      <tr key={p.id}><td className="px-4 py-2 font-medium">{p.partner_name}</td>
                      <td className="px-4 py-2">{p.service_needed}</td>
                      <td className="px-4 py-2">{p.commission_pct}%</td>
                      <td className="px-4 py-2 font-bold">${p.payout_amount}</td>
                      <td className="px-4 py-2">{p.payout_processed ? <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Paid</span> : <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Pending</span>}</td>
                      <td className="px-4 py-2">{!p.payout_processed && (
                        <Button size="sm" className="text-xs rounded bg-[#D4A843] text-[#0A0A0A] font-semibold" onClick={async () => {
                          try {
                            const { data } = await api.post(`/api/referrals/payouts/${p.id}/process`, { origin_url: window.location.origin });
                            if (data.url) window.location.href = data.url;
                          } catch { toast.error('Payout failed'); }
                        }} data-testid={`process-${p.id}`}><DollarSign className="w-3 h-3 mr-1" />Process</Button>
                      )}</td></tr>
                    ))}
                  </tbody></table>
                </div>
              )}
            </div>
          )}

          {/* Business Health Dashboard */}
          {view === 'health' && (
            <BusinessHealthView
              auditForm={auditForm}
              gbpChecklist={gbpChecklist}
              setGbpChecklist={setGbpChecklist}
              authorities={authorities}
              setAuthorities={setAuthorities}
              gbpCheckState={gbpCheckState}
              setGbpCheckState={setGbpCheckState}
              latestAudit={audits[0]}
            />
          )}
        </div>
      </main>
    </div>
  );
}

function BusinessHealthView({ auditForm, gbpChecklist, setGbpChecklist, authorities, setAuthorities, gbpCheckState, setGbpCheckState, latestAudit }) {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [gbpRes, authRes] = await Promise.all([
          api.get('/api/growth/gbp-checklist'),
          api.get(`/api/growth/authorities?industry=${auditForm.industry || 'general'}`),
        ]);
        setGbpChecklist(gbpRes.data.checklist || []);
        setAuthorities(authRes.data.authorities || []);
      } catch (e) { /* handled */ } finally { setLoading(false); }
    })();
  }, [auditForm.industry, setGbpChecklist, setAuthorities]);

  const toggleCheck = (id) => setGbpCheckState(prev => ({ ...prev, [id]: !prev[id] }));
  const checkedCount = Object.values(gbpCheckState).filter(Boolean).length;
  const totalChecks = gbpChecklist.length;
  const healthPct = totalChecks > 0 ? Math.round((checkedCount / totalChecks) * 100) : 0;

  const latestGbp = latestAudit?.analysis?.gbp_health;
  const latestAuthorities = latestAudit?.analysis?.governing_authorities_detail || [];

  const PRIORITY_COLORS = { critical: 'border-red-200 bg-red-50', high: 'border-orange-200 bg-orange-50', medium: 'border-amber-200 bg-amber-50', low: 'border-gray-200 bg-gray-50' };
  const AUTH_STATUS = { compliant: 'bg-emerald-100 text-emerald-700', needs_review: 'bg-amber-100 text-amber-700', non_compliant: 'bg-red-100 text-red-700', unknown: 'bg-gray-100 text-gray-500' };
  const categories = [...new Set(gbpChecklist.map(c => c.category))];
  const authTypes = [...new Set(authorities.map(a => a.type))];

  if (loading) return <div className="flex items-center justify-center py-20"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6" data-testid="business-health-view">
      {/* Health Score */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center hover-lift" data-testid="gbp-health-score">
          <ScoreRing score={healthPct} size={90} />
          <p className="text-sm font-bold text-gray-900 mt-2">GBP Health Check</p>
          <p className="text-[10px] text-gray-500">{checkedCount}/{totalChecks} checks completed</p>
        </div>
        {latestGbp && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center hover-lift">
            <p className="text-4xl font-bold text-[#D4A843]">{latestGbp.overall_grade}</p>
            <p className="text-sm font-bold text-gray-900 mt-2">AI GBP Grade</p>
            <div className="grid grid-cols-2 gap-2 mt-3 text-[10px]">
              <div><p className="font-bold text-gray-900">{latestGbp.core_accuracy_score}%</p><p className="text-gray-500">Accuracy</p></div>
              <div><p className="font-bold text-gray-900">{latestGbp.reputation_score}%</p><p className="text-gray-500">Reputation</p></div>
              <div><p className="font-bold text-gray-900">{latestGbp.visual_freshness_score}%</p><p className="text-gray-500">Visual</p></div>
              <div><p className="font-bold text-gray-900">{latestGbp.kpi_readiness_score}%</p><p className="text-gray-500">KPIs</p></div>
            </div>
          </div>
        )}
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center hover-lift" data-testid="authority-count">
          <p className="text-4xl font-bold text-[#D4A843]">{authorities.length}</p>
          <p className="text-sm font-bold text-gray-900 mt-2">Governing Authorities</p>
          <p className="text-[10px] text-gray-500">For: {auditForm.industry || 'General'} industry</p>
        </div>
      </div>

      {/* GBP Checklist */}
      <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="gbp-checklist-section">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Globe className="w-4 h-4 text-[#D4A843]" /> Google Business Profile Monthly Checklist
        </h3>
        {categories.map(cat => (
          <div key={cat} className="mb-4">
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">{cat}</p>
            <div className="space-y-2">
              {gbpChecklist.filter(c => c.category === cat).map(item => (
                <label key={item.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${gbpCheckState[item.id] ? 'bg-emerald-50 border-emerald-200' : PRIORITY_COLORS[item.priority]}`} data-testid={`gbp-check-${item.id}`}>
                  <input type="checkbox" checked={!!gbpCheckState[item.id]} onChange={() => toggleCheck(item.id)} className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#D4A843] focus:ring-[#D4A843]" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-medium ${gbpCheckState[item.id] ? 'text-emerald-700 line-through' : 'text-gray-900'}`}>{item.title}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${item.priority === 'critical' ? 'bg-red-100 text-red-700' : item.priority === 'high' ? 'bg-orange-100 text-orange-700' : item.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>{item.priority}</span>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-0.5">{item.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Governing Authorities */}
      <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="authorities-section">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-[#D4A843]" /> Governing Authorities — {auditForm.industry || 'General'}
        </h3>
        {authTypes.map(type => (
          <div key={type} className="mb-4">
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">{type}</p>
            <div className="space-y-2">
              {authorities.filter(a => a.type === type).map((auth, i) => {
                const aiStatus = latestAuthorities.find(la => la.name?.toLowerCase().includes(auth.name.toLowerCase().split(' ')[0]));
                return (
                  <div key={`item-${i}`} className="border border-gray-200 rounded-lg p-4" data-testid={`authority-${auth.name.replace(/\s/g, '-')}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold text-gray-900">{auth.name}</p>
                        <span className="text-[10px] bg-[#D4A843]/10 text-[#D4A843] px-2 py-0.5 rounded font-medium">{auth.category}</span>
                      </div>
                      {aiStatus && <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${AUTH_STATUS[aiStatus.status] || AUTH_STATUS.unknown}`}>{aiStatus.status?.replace('_', ' ')}</span>}
                    </div>
                    {auth.url_template && <a href={auth.url_template} target="_blank" rel="noreferrer" className="text-[10px] text-[#D4A843] hover:underline font-mono">{auth.url_template}</a>}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {auth.requirements.map((r, j) => (
                        <span key={`j-${j}`} className="text-[10px] bg-gray-50 border border-gray-200 px-2 py-0.5 rounded">{r}</span>
                      ))}
                    </div>
                    {aiStatus?.action_items?.length > 0 && (
                      <div className="mt-2 bg-amber-50 rounded p-2"><p className="text-[10px] font-bold text-amber-700">Action Items:</p>
                        {aiStatus.action_items.map((a, k) => <p key={`ai-action-${k}-${String(a).slice(0, 20)}`} className="text-[10px] text-amber-600">- {a}</p>)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

